import React from "react";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Select, Typography, Spin, Avatar, Card } from "antd";
import UserHOC from "../../../components/shared/HOC/UserHOC";
import { useQuizAttemptDetails, tryParseJSON, formatDate } from "../../../hooks/UserHooks/MyCoursesHook/useQuizAttemptDetails";
import "./QuizAttemptDetailsPage.css";

const { Option } = Select;
const { Title, Text } = Typography;


const StatCard = ({ label, value, sub }) => (
  <div className="stat-card">
    <span className="stat-card-value">
      {value}
      {sub && <span className="stat-card-sub">/{sub}</span>}
    </span>
    <span className="stat-card-label">{label}</span>
  </div>
);

const PassFailBadge = ({ passed }) => (
  <div className="pass-fail-badge">
    {passed ? (
      <div className="pass-fail-inner">
        <CheckCircleOutlined className="pass-icon" />
        <span className="pass-label">Passed</span>
      </div>
    ) : (
      <div className="pass-fail-inner">
        <CloseCircleOutlined className="fail-icon" />
        <span className="fail-label">Failed</span>
      </div>
    )}
  </div>
);

const UserInfoCard = ({ userName, userRole, userAvatar }) => (
  <div className="user-info-card">
    <Avatar
      size={52}
      src={userAvatar}
      className="user-avatar"
    >
      {!userAvatar && userName?.charAt(0)?.toUpperCase()}
    </Avatar>
    <div>
      <div className="user-name">{userName}</div>
      <div className="user-role">{userRole}</div>
    </div>
  </div>
);

const ScoreSummaryBar = ({ score, timeTaken, submissionType, passingMarks, passed }) => (
  <div className="score-summary-bar">
    <UserInfoCard {...score} />
    <div className="score-stats-row">
      <StatCard label="Marks Obtained" value={score?.totalScore} sub={score?.maxPossibleScore} />
      <StatCard label="Percentage" value={`${score?.percentageScore}%`} />
      <StatCard label="Correct Answers" value={score?.correctAnswers} sub={score?.totalQuestions} />
      {timeTaken && <StatCard label="Time Taken" value={timeTaken} />}
      <StatCard label="Submission Type" value={submissionType} />
      {passingMarks && <StatCard label="Passing Marks" value={passingMarks} sub={score?.maxPossibleScore} />}
      <PassFailBadge passed={passed} />
    </div>
  </div>
);

const OptionRow = ({ option, selected, isCorrectAnswer }) => {
  let cls = "option-row";
  if (selected && isCorrectAnswer) cls += " option-row--correct-selected";
  else if (selected && !isCorrectAnswer) cls += " option-row--wrong-selected";
  else if (!selected && isCorrectAnswer) cls += " option-row--missed-correct";

  return (
    <div className={cls}>
      <span className="option-dot" />
      {option}
    </div>
  );
};

const ShortAnswerRow = ({ userAnswers, correctAnswers, isCorrect }) => {
  const noAnswerGiven = userAnswers.length === 0;
  return (
    <div>
      <div className={`short-answer-box ${noAnswerGiven ? "short-answer-box--missed" : isCorrect ? "short-answer-box--correct" : "short-answer-box--wrong"}`}>
        {noAnswerGiven ? (
          <Text className="answer-text-missed">
            <b>No answer provided</b>{" "}
            <span className="answer-text-missed-sub">(Missed)</span>
          </Text>
        ) : (
          <Text className={isCorrect ? "answer-text-correct" : "answer-text-wrong"}>
            <b>Your Answer: </b>{userAnswers[0]}
          </Text>
        )}
      </div>

      {!isCorrect && (
        <div className="short-answer-box short-answer-box--missed">
          <Text className="answer-text-missed">
            <b>Correct Answer:</b> {correctAnswers?.[0]}
          </Text>
        </div>
      )}

      {isCorrect && (
        <div className="short-answer-box short-answer-box--correct">
          <Text className="answer-text-correct"><b>Correct Answer!</b></Text>
        </div>
      )}
    </div>
  );
};

const QuestionCard = ({ response, index }) => {
  const userAnswers = tryParseJSON(response.userAnswer);
  const correctAnswers = tryParseJSON(response.correctAnswer);
  const allOptions = tryParseJSON(response.options || "");
  const { isCorrect, pointsEarned } = response;

  return (
    <div className="question-card">
      <div className="question-card-header">
        <div className="question-card-text">
          Q{index + 1}. {response.questionText}
        </div>
        <span className={`question-points ${isCorrect ? "question-points--correct" : "question-points--wrong"}`}>
          +{pointsEarned}
        </span>
      </div>

      {allOptions.length > 0 ? (
        allOptions.map((option, idx) => (
          <OptionRow
            key={idx}
            option={option}
            selected={userAnswers.includes(option)}
            isCorrectAnswer={correctAnswers.includes(option)}
          />
        ))
      ) : (
        <ShortAnswerRow
          userAnswers={userAnswers}
          correctAnswers={correctAnswers}
          isCorrect={isCorrect}
        />
      )}
    </div>
  );
};

const Legend = () => (
  <div className="legend-bar">
    {[
      { color: "#088A15", label: "Correct" },
      { color: "#ff4d4f", label: "Incorrect" },
      { color: "#1777FF", label: "Missed" },
      { color: "#722ed1", label: "Marked for review" },
    ].map(({ color, label }) => (
      <div key={label} className="legend-item">
        <span className="legend-dot" style={{ backgroundColor: color }} />
        <span className="legend-label">{label}</span>
      </div>
    ))}
  </div>
);


const QuizAttemptDetailsPage = ({ setHeader, setBreadCrumbs, setHideSidebar }) => {
  const {
    quizName,
    userRole,
    userAvatar,
    userName,
    attempts,
    selectedAttemptIndex,
    setSelectedAttemptIndex,
    selectedAttempt,
    score,
    passed,
    timeTaken,
    submissionType,
    passingMarks,
    loading,
  } = useQuizAttemptDetails(setHideSidebar);

  const formatSubmissionType = (type) =>
    type?.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) ?? "";

  return (
    <Card className="attempt-details-card">
      <div className="attempt-details-header">
        <Title level={4} className="attempt-details-title">
          {quizName}
          {score?.submittedAt && (
            <span className="attempt-details-date">
              ({formatDate(score.submittedAt)})
            </span>
          )}
        </Title>

        <Select
          value={selectedAttemptIndex}
          onChange={setSelectedAttemptIndex}
          className="attempt-select"
        >
          {attempts.map((item, index) => (
            <Option key={item.attemptId} value={index}>
              Attempt-{item.attemptNumber}  {/* ← was item.quizAttempt.attempt */}
            </Option>
          ))}
        </Select>
      </div>

      {loading ? (
        <div className="loading-container">
          <Spin size="large" />
        </div>
      ) : selectedAttempt ? (
        <>
          <div className="score-summary-bar">
            <UserInfoCard
              userName={userName}
              userRole={userRole}
              userAvatar={userAvatar}
            />
            <div className="score-stats-row">
              <StatCard label="Marks Obtained" value={score?.totalScore} sub={score?.maxPossibleScore} />
              <StatCard label="Percentage" value={`${score?.percentageScore}%`} />
              <StatCard label="Correct Answers" value={score?.correctAnswers} sub={score?.totalQuestions} />
              {timeTaken && <StatCard label="Time Taken" value={timeTaken} />}
              {passingMarks && <StatCard label="Passing Percentage" value={`${passingMarks}%`} />}
              <StatCard label="Submission Type" value={formatSubmissionType(submissionType)} />
              <PassFailBadge passed={passed} />
            </div>
          </div>

          {(selectedAttempt?.userResponses || []).map((response, index) => (
            <QuestionCard key={response.responseId} response={response} index={index} />
          ))}

          <Legend />
        </>
      ) : (
        <div className="empty-state">No attempt data found.</div>
      )}
    </Card>
  );
};

export default UserHOC(QuizAttemptDetailsPage);